/* eslint-disable import/prefer-default-export */

import { achBearerToken, maybeReauthOnAchUnauthorized } from '../../util/achAuth';

// Shared request helper for ACH: cross-origin GET. Auth is a Bearer — the user's
// opaque `sso_token`, which ACH introspects via the Hub (ACH-2th4; jwt_token is
// the Ventura X-Token and is rejected). VITE_ACH_UP_SESSION overrides in dev.
// The `_up-session` cookie (credentials: 'include') remains as a fallback for
// UP-Hub-embedded contexts. Cookie-only was not enough: outside UP Hub there is
// no ACH-origin cookie, so KPI/readiness 401'd (user-reported 2026-07-20).
export async function achGetJSON(achApiHost: string, path: string): Promise<any> {
  const token = achBearerToken();
  const response = await fetch(`${achApiHost}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  // ACH is a supplementary data source (KPI/Risk columns) and is NOT the session
  // authority — Ventura is. A 401 with a token attached means the sso_token went
  // stale while the Hub SSO session is (normally) still live, so auto-reauth ONCE
  // through the existing SSO flow (ACH-2423 stopgap; never a logout). A recurring
  // 401 inside the guard window falls through to the throw below (no loop).
  if (response.status === 401 && (await maybeReauthOnAchUnauthorized(response.status, Boolean(token)))) {
    return new Promise<never>(() => {}); // navigating away via SSO; never settle
  }
  if (!response.ok) {
    const body = await response.text();
    throw new Error(`ACH request failed (${response.status}): ${path}${body ? ` — ${body}` : ''}`);
  }

  return response.json();
}

// achSendJSON is the write counterpart of achGetJSON: a cross-origin POST/PATCH with
// the same Bearer/cookie auth. Unlike the read path, ACH board writes (acknowledge)
// are user-initiated actions, so a failure IS surfaced (thrown) for the mutation to
// report — the caller shows an error and rolls back the optimistic update.
export async function achSendJSON(
  achApiHost: string,
  path: string,
  method: 'POST' | 'PATCH',
  body?: unknown
): Promise<any> {
  const token = achBearerToken();
  const response = await fetch(`${achApiHost}${path}`, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Same one-shot silent re-auth as the read path (ACH-2423 stopgap): a 401 with
  // a token attached bounces once through SSO and returns here; a recurring 401
  // inside the guard window falls through to the surfaced throw below.
  if (response.status === 401 && (await maybeReauthOnAchUnauthorized(response.status, Boolean(token)))) {
    return new Promise<never>(() => {}); // navigating away via SSO; never settle
  }
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`ACH request failed (${response.status}): ${path}${text ? ` — ${text}` : ''}`);
  }

  return response.json();
}
